import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query'
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'

const CACHE_NAME = 'directbilling-api-data'
const CACH_EXPIRY_MILLISECONDS = 60 * 60 * 1000

const CACHE_ENDPOINTS = [
  'getInsurersPublic',
  'getInsurerDetails',
  'getInsuranceProvider',
  'getDefaultForms',
  'getInsurancePlans',
] as const

const CLEANUP_ON_LOGOUT = ['getInsurancePlans']

type CachedEndpoint = (typeof CACHE_ENDPOINTS)[number]

type CachedData<T> = {
  data: T
  meta: {
    response: {
      ok: boolean
      status: number
    }
  }
}

type PutCachePayload<T> = QueryReturnValue<T, FetchBaseQueryError, FetchBaseQueryMeta>

export class ApiCache {
  #endpoint: string

  constructor(endpoint: string) {
    this.#endpoint = endpoint
  }

  get isCacheable(): boolean {
    return CACHE_ENDPOINTS.includes(this.#endpoint as CachedEndpoint) || this.#endpoint.endsWith('Configuration')
  }

  /**
   * @returns the cached response for the given url or null
   */
  fetchFromCache = async <T = unknown>(url: string): Promise<CachedData<T> | null> => {
    try {
      const cache = await caches.open(CACHE_NAME)
      const cachedResponse = await cache.match(new URL(url))
      if (cachedResponse) {
        if (ApiCache.isExpired(cachedResponse)) {
          await cache.delete(url)
          return null
        }

        console.debug('directbilling api cache - fetched from cache ', url)
        const data = await cachedResponse.json()
        return {
          data,
          meta: {
            response: {
              ok: true,
              status: 200,
            },
          },
        }
      }
      return null
    } catch (error) {
      console.warn('directbilling api cache error', error)
      return null
    }
  }

  private static isExpired = (cachedResponse?: Response): boolean => {
    const dateHeader = cachedResponse?.headers.get('Date')
    if (dateHeader && new Date(Number(dateHeader)).getTime() + CACH_EXPIRY_MILLISECONDS < Date.now()) {
      console.debug('directbilling api cache - cached data expired')
      return true
    }
    return false
  }

  /**
   * Store valid response (only status 200) in cache
   */
  putInCache = async <T = unknown>(payload: PutCachePayload<T>): Promise<void> => {
    if (!payload.meta?.request || payload.error || !payload.meta?.response?.ok) return

    try {
      const cache = await caches.open(CACHE_NAME)
      const url = payload.meta.request.url
      cache.put(
        url,
        new Response(JSON.stringify(payload.data ?? {}), {
          status: payload.meta?.response?.status ?? 200,
          headers: this.getResponseHeaders(payload.meta?.response?.headers),
        })
      )

      console.log('directbilling api cache - put data ', url)
    } catch (error) {
      console.warn('directbilling put cache error', error)
    }
  }

  private getResponseHeaders = (headers?: Headers): Record<string, string> => {
    const result = {
      Date: `${Date.now()}`, // used to calculate cache expiry
      Endpoint: this.#endpoint, // used in cleanup cache
    }

    result['Content-Type'] = headers?.get('Content-Type') ?? 'text/plain'
    result['Vary'] = headers?.get('Vary') ?? ''

    return result
  }

  /**
   * Delete the cached entry for the specified endpoint
   */
  static delete = async (endpoint?: CachedEndpoint): Promise<void> => {
    try {
      const cache = await caches.open(CACHE_NAME)
      const keys = await cache.keys()
      keys.forEach(async request => {
        const cachedResponse = await cache.match(request)
        const cachedEndpoint = cachedResponse?.headers?.get('Endpoint')

        // delete cache for specified entries
        if (cachedEndpoint && cachedEndpoint === endpoint) {
          return cache.delete(request)
        }
      })
      return Promise.resolve()
    } catch (error) {
      console.warn('directbilling api cache cleanup error', error)
      return Promise.resolve()
    }
  }

  /**
   * Cleanup cache as needed, e.g. on logout.
   */
  static cleanupCache = async (): Promise<void> => {
    try {
      const cache = await caches.open(CACHE_NAME)
      const keys = await cache.keys()
      keys.forEach(async request => {
        const cachedResponse = await cache.match(request)
        const endpoint = cachedResponse?.headers?.get('Endpoint')

        // delete from cache if expired or if included in CLEANUP_ON_LOGOUT list
        if (ApiCache.isExpired(cachedResponse) || (endpoint && CLEANUP_ON_LOGOUT.includes(endpoint))) {
          cache.delete(request)
        }
      })
    } catch (error) {
      console.warn('directbilling api cache cleanup error', error)
    }
  }
}
